import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, Users, Building2, TrendingUp, Layers, ArrowLeft } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useApi } from '../../lib/useApi';
import LoadingSpinner from '../common/LoadingSpinner';
import type { Account } from '../../types';
import { resolveLogoUrl } from '../../lib/logo';

interface TeamMemberSummary {
  user_id: string;
  full_name: string;
  email: string;
  role: string;
  account_count: number;
  total_tam_min: number;
  total_tam_max: number;
  territory_health_score: number;
  tier_a_count: number;
  tier_b_count: number;
  tier_c_count: number;
  subordinate_count: number;
  avg_use_case_score: number | null;
}

interface BreadcrumbEntry {
  userId: string;
  name: string;
}

function formatTam(min: number, max: number): string {
  const format = (n: number) => {
    if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
    return `$${n}`;
  };
  if (min === 0 && max === 0) return '-';
  if (min === max) return format(max);
  return `${format(min)} - ${format(max)}`;
}

export default function TeamDrillDown() {
  const { user, organisation, membership } = useAuth();
  const { apiGet } = useApi();
  const navigate = useNavigate();
  const [breadcrumb, setBreadcrumb] = useState<BreadcrumbEntry[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMemberSummary[]>([]);
  const [memberAccounts, setMemberAccounts] = useState<Account[] | null>(null);
  const [viewingMemberName, setViewingMemberName] = useState('');
  const [loading, setLoading] = useState(true);

  const currentUserId = breadcrumb.length > 0
    ? breadcrumb[breadcrumb.length - 1].userId
    : user?.id;

  const loadTeam = useCallback(async (targetUserId: string) => {
    if (!organisation) return;
    setLoading(true);
    setMemberAccounts(null);

    try {
      const { members } = await apiGet<{ members: TeamMemberSummary[] }>(
        `/v1/team-summary?org_id=${encodeURIComponent(organisation.id)}&user_id=${encodeURIComponent(targetUserId)}`,
      );
      setTeamMembers(members || []);
    } catch {
      setTeamMembers([]);
    }
    setLoading(false);
  }, [apiGet, organisation]);

  useEffect(() => {
    if (!user?.id || !organisation) return;
    const targetId = breadcrumb.length > 0
      ? breadcrumb[breadcrumb.length - 1].userId
      : user.id;
    loadTeam(targetId);
  }, [user?.id, organisation, breadcrumb, loadTeam]);

  const handleDrillIntoMember = (member: TeamMemberSummary) => {
    if (member.subordinate_count > 0) {
      setBreadcrumb(prev => [...prev, { userId: member.user_id, name: member.full_name }]);
    } else {
      loadMemberAccounts(member.user_id, member.full_name);
    }
  };

  const loadMemberAccounts = async (memberId: string, memberName: string) => {
    if (!organisation) return;
    setLoading(true);
    setViewingMemberName(memberName);

    try {
      const { accounts } = await apiGet<{ accounts: Account[] }>(
        `/v1/team-member-accounts?org_id=${encodeURIComponent(organisation.id)}&user_id=${encodeURIComponent(memberId)}`,
      );
      setMemberAccounts(accounts || []);
    } catch {
      setMemberAccounts([]);
    }
    setLoading(false);
  };

  const handleBack = () => {
    if (memberAccounts !== null) {
      setMemberAccounts(null);
      setViewingMemberName('');
      return;
    }
    if (breadcrumb.length > 0) {
      setBreadcrumb(prev => prev.slice(0, -1));
    }
  };

  const handleBreadcrumbClick = (index: number) => {
    if (memberAccounts !== null) {
      setMemberAccounts(null);
      setViewingMemberName('');
    }
    if (index === -1) {
      setBreadcrumb([]);
    } else {
      setBreadcrumb(prev => prev.slice(0, index + 1));
    }
  };

  const myName = user?.user_metadata?.full_name || 'My Team';
  const roleName = membership?.role === 'executive' ? 'Theatre' : 'Team';

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div>
      {/* Breadcrumb */}
      <div className="flex items-center gap-1.5 mb-6 text-sm">
        {(breadcrumb.length > 0 || memberAccounts !== null) && (
          <button onClick={handleBack} className="p-1 rounded hover:bg-app-canvas mr-1 transition-colors">
            <ArrowLeft className="w-4 h-4 text-app-muted" />
          </button>
        )}
        <button
          onClick={() => handleBreadcrumbClick(-1)}
          className={`font-medium transition-colors ${breadcrumb.length === 0 && !memberAccounts ? 'text-app-ink' : 'text-forest-600 hover:text-forest-700'}`}
        >
          {myName}'s {roleName}
        </button>
        {breadcrumb.map((entry, i) => (
          <span key={entry.userId} className="flex items-center gap-1.5">
            <ChevronRight className="w-3.5 h-3.5 text-app-muted" />
            <button
              onClick={() => handleBreadcrumbClick(i)}
              className={`font-medium transition-colors ${i === breadcrumb.length - 1 && !memberAccounts ? 'text-app-ink' : 'text-forest-600 hover:text-forest-700'}`}
            >
              {entry.name}
            </button>
          </span>
        ))}
        {memberAccounts !== null && (
          <span className="flex items-center gap-1.5">
            <ChevronRight className="w-3.5 h-3.5 text-app-muted" />
            <span className="font-medium text-app-ink">{viewingMemberName}'s Accounts</span>
          </span>
        )}
      </div>

      {/* Member accounts view */}
      {memberAccounts !== null && (
        <div>
          {memberAccounts.length === 0 ? (
            <div className="text-center py-16">
              <Building2 className="w-12 h-12 text-app-line-strong mx-auto mb-4" />
              <h3 className="text-lg font-medium text-app-ink mb-1">No accounts</h3>
              <p className="text-sm text-app-muted">{viewingMemberName} has no accounts in their territory.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {memberAccounts.map(account => (
                <MiniAccountCard key={account.id} account={account} onClick={() => navigate(`/app/accounts/${account.id}`)} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Team members grid */}
      {memberAccounts === null && (
        <div>
          {teamMembers.length === 0 ? (
            <div className="text-center py-16">
              <Users className="w-12 h-12 text-app-line-strong mx-auto mb-4" />
              <h3 className="text-lg font-medium text-app-ink mb-1">No direct reports</h3>
              <p className="text-sm text-app-muted">No team members report to this user.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {teamMembers.map(member => (
                <TeamMemberCard key={member.user_id} member={member} onClick={() => handleDrillIntoMember(member)} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TeamMemberCard({ member, onClick }: { member: TeamMemberSummary; onClick: () => void }) {
  const roleColors: Record<string, string> = {
    executive: 'bg-app-warning/10 text-app-warning border-app-warning',
    leader: 'bg-app-focus/10 text-app-focus border-app-focus',
    admin: 'bg-forest-50 text-forest-700 border-forest-100',
    member: 'bg-app-canvas text-app-muted border-app-line',
  };
  const badge = roleColors[member.role] || roleColors.member;
  const healthColor = member.territory_health_score >= 70
    ? 'text-app-positive'
    : member.territory_health_score >= 45
    ? 'text-app-warning'
    : 'text-app-muted';

  return (
    <div
      onClick={onClick}
      className="bg-app-surface rounded-lg border border-app-line shadow-none p-5 cursor-pointer hover:border-forest-200 hover:shadow-none transition group"
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-forest-50 rounded-lg flex items-center justify-center">
            <span className="text-sm font-bold text-forest-600">
              {member.full_name.charAt(0).toUpperCase()}
            </span>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-app-ink group-hover:text-forest-700 transition-colors">
              {member.full_name}
            </h3>
            <span className={`inline-block text-[10px] font-medium px-2 py-0.5 rounded-full border ${badge}`}>
              {member.role.charAt(0).toUpperCase() + member.role.slice(1)}
            </span>
          </div>
        </div>
        {member.subordinate_count > 0 && (
          <div className="flex items-center gap-1 text-xs text-app-muted">
            <Users className="w-3.5 h-3.5" />
            <span>{member.subordinate_count}</span>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="p-2.5 bg-app-canvas rounded-lg">
          <p className="text-[10px] font-medium text-app-muted uppercase tracking-wide">Accounts</p>
          <p className="text-lg font-bold text-app-ink">{member.account_count}</p>
        </div>
        <div className="p-2.5 bg-app-canvas rounded-lg">
          <p className="text-[10px] font-medium text-app-muted uppercase tracking-wide">Health</p>
          <p className={`text-lg font-bold ${healthColor}`}>
            {member.territory_health_score > 0 ? Math.round(member.territory_health_score) : '-'}
          </p>
        </div>
        <div className="p-2.5 bg-app-canvas rounded-lg">
          <p className="text-[10px] font-medium text-app-muted uppercase tracking-wide">UC Score</p>
          <p className="text-lg font-bold text-app-ink">
            {member.avg_use_case_score ? (member.avg_use_case_score).toFixed(1) : '-'}
          </p>
        </div>
        <div className="p-2.5 bg-app-canvas rounded-lg">
          <p className="text-[10px] font-medium text-app-muted uppercase tracking-wide">TAM</p>
          <p className="text-sm font-semibold text-app-ink">
            {formatTam(member.total_tam_min, member.total_tam_max)}
          </p>
        </div>
        <div className="p-2.5 bg-app-canvas rounded-lg col-span-2">
          <div className="flex items-center gap-2">
            <TierBadge tier="A" count={member.tier_a_count} />
            <TierBadge tier="B" count={member.tier_b_count} />
            <TierBadge tier="C" count={member.tier_c_count} />
          </div>
        </div>
      </div>

      <div className="mt-3 flex items-center justify-end text-xs text-forest-600 font-medium opacity-0 group-hover:opacity-100 transition-opacity">
        {member.subordinate_count > 0 ? 'View team' : 'View accounts'}
        <ChevronRight className="w-3.5 h-3.5 ml-0.5" />
      </div>
    </div>
  );
}

function TierBadge({ tier, count }: { tier: string; count: number }) {
  if (count === 0) return null;
  const colors: Record<string, string> = {
    A: 'bg-app-positive/10 text-app-positive',
    B: 'bg-app-warning/10 text-app-warning',
    C: 'bg-app-canvas text-app-muted',
  };
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded ${colors[tier] || colors.C}`}>
      <Layers className="w-2.5 h-2.5" /> {tier}: {count}
    </span>
  );
}

function MiniAccountCard({ account, onClick }: { account: Account; onClick: () => void }) {
  const [logoFailed, setLogoFailed] = useState(false);
  const logoSrc = resolveLogoUrl(account.logo_url, account.website_url);
  const scoreColor = (account.tier_score || 0) >= 70
    ? 'text-app-positive'
    : (account.tier_score || 0) >= 45
    ? 'text-app-warning'
    : 'text-app-muted';

  return (
    <div
      onClick={onClick}
      className="bg-app-surface rounded-lg border border-app-line shadow-none p-4 cursor-pointer hover:border-forest-200 hover:shadow-none transition"
    >
      <div className="flex items-center gap-3 mb-3">
        {logoSrc && !logoFailed ? (
          <img src={logoSrc} alt="" className="w-9 h-9 rounded-lg object-cover bg-app-canvas" onError={() => setLogoFailed(true)} />
        ) : (
          <div className="w-9 h-9 bg-forest-50 rounded-lg flex items-center justify-center">
            <span className="text-sm font-bold text-forest-600">{account.company_name.charAt(0)}</span>
          </div>
        )}
        <div className="min-w-0">
          <h4 className="text-sm font-semibold text-app-ink truncate">{account.company_name}</h4>
          {account.industry && <p className="text-xs text-app-muted truncate">{account.industry}</p>}
        </div>
      </div>
      <div className="flex items-center gap-3 text-xs text-app-muted">
        {account.tier && (
          <span className="flex items-center gap-1">
            <Layers className="w-3 h-3" /> Tier {account.tier}
          </span>
        )}
        {(account.tier_score || 0) > 0 && (
          <span className={`flex items-center gap-1 font-medium ${scoreColor}`}>
            <TrendingUp className="w-3 h-3" /> {Math.round(account.tier_score)}
          </span>
        )}
        {account.employee_count > 0 && (
          <span className="flex items-center gap-1">
            <Users className="w-3 h-3" /> {account.employee_count.toLocaleString()}
          </span>
        )}
      </div>
    </div>
  );
}
